import { motion } from 'framer-motion';
import { Plug, BrainCircuit, Rocket } from 'lucide-react';

const steps = [
  {
    icon: Plug,
    step: '01',
    title: 'Connect your channels',
    description:
      'Link Instagram, Facebook, LinkedIn, TikTok, and Google Ads in under two minutes. No code, no IT ticket.',
  },
  {
    icon: BrainCircuit,
    step: '02',
    title: 'Let AI study your brand',
    description:
      'SprynAI learns your voice, audience, and goals — then drafts a 90-day content and ads strategy tuned to your numbers.',
  },
  {
    icon: Rocket,
    step: '03',
    title: 'Launch, measure, scale',
    description:
      'One-click publishing, automatic A/B testing, and weekly AI-written reports that tell you exactly what to ship next.',
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how"
      className="relative bg-background text-foreground py-24 md:py-32 px-8 md:px-28"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-16 md:mb-20"
        >
          <div className="liquid-glass inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-6">
            <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              How it works
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-medium tracking-[-1.5px] leading-[1.05]">
            From zero to compounding growth in{' '}
            <span className="font-serif italic font-normal">three steps.</span>
          </h2>
        </motion.div>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Animated connecting line — draws left→right when section enters view (desktop only) */}
          <motion.div
            aria-hidden
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: '0% 50%' }}
            className="hidden md:block absolute top-[68px] left-[14%] right-[14%] h-px bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none will-change-transform"
          />
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="relative rounded-2xl border border-border bg-card p-7 md:p-8 transition-colors hover:bg-white/[0.04] hover:border-white/15 transform-gpu"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="liquid-glass inline-flex items-center justify-center h-11 w-11 rounded-xl">
                    <Icon className="h-5 w-5 text-foreground" />
                  </div>
                  <span className="font-serif italic text-3xl text-muted-foreground">
                    {s.step}
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-semibold tracking-tight mb-3">
                  {s.title}
                </h3>
                <p className="text-sm md:text-base leading-6 text-muted-foreground">
                  {s.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
